import { concat, defer, Observable } from 'rxjs';
import { UNAUTHORIZED_PATH } from '@pass-frontend/common/constants/paths';
import { GQL } from '@pass-frontend/data-access/state-management/apollo';
import {
  LWR_AUTH,
  SESSION_EXPIRES_SOON,
  SESSION_EXPIRY_UPDATED,
  getSessionExpiryMsec,
  SESSION_EXPIRED,
  SESSION_UNAUTHENTICATED,
  SESSION_UNAUTHORIZED,
} from '@pass-frontend/features/authentication/util';
import { env } from '@pass-frontend/features/configuration/util';
import { eventBus, after } from '@pass-frontend/utility/event-bus';

import { trace } from '@pass-frontend/utility/swiss-knife';
import { huginnLoader, HUGINN_LOAD, provisionSession } from './huginn-helpers';

const { ENABLE_LWR, BASE_PATH } = env();

// Handle if their session ended (through any outside means)
export const provisionOnFailedOrExpiredSession = eventBus.listen(
  (e) => {
    return (
      (GQL.error.match(e) &&
        e.payload.errors.some((e) =>
          [SESSION_UNAUTHENTICATED].includes(e.message as string),
        )) ||
      SESSION_EXPIRED.match(e)
    );
  },
  () => {
    // The LWR-team-recommended way of handling expiry.
    provisionSession('/');
  },
);

eventBus.listen(
  (e) => {
    return (
      GQL.error.match(e) &&
      e.payload.errors.some(
        (e) =>
          (e as string) === SESSION_UNAUTHORIZED ||
          e.message === SESSION_UNAUTHORIZED,
      )
    );
  },
  () => {
    trace('session/unauthorized');
    if (!window.location.pathname.includes(UNAUTHORIZED_PATH)) {
      window.location.href = BASE_PATH + UNAUTHORIZED_PATH;
    }
  },
);

export const GQL_DEBOUNCE_TIME_MS = 2000;
export const SESSION_WARN_TIMEOUT_SECONDS = 60;
export const LARGEST_JS_DATE = 8640000000000000;

// Provide the means to notify of session extension as graphql calls extend our session
// Note - switching (aka replacing) a delayed effect amounts to debouncing it.
export const sessionExpiryListener = eventBus.listenSwitching(
  GQL.next.match,
  () => {
    // Updated once our debounce settles
    let msecTillWarn = LARGEST_JS_DATE;

    // This chain of delayed event triggerings is begun, every new GQL.next event, and cancels any previous
    return concat(
      after(GQL_DEBOUNCE_TIME_MS, () => {
        const newExpiry = getSessionExpiryMsec();
        const warnTime = newExpiry - SESSION_WARN_TIMEOUT_SECONDS * 1000;
        msecTillWarn = warnTime - Date.now();

        const expiresAt = new Date(newExpiry).toLocaleString();
        eventBus.trigger(
          SESSION_EXPIRY_UPDATED({
            expiry: newExpiry,
            expiresAt,
          }),
        );
      }),
      // deferred, to avoid evaluating msecTillWarn until after GQL_DEBOUNCE_TIME_MS
      defer(() =>
        after(msecTillWarn, () => {
          eventBus.trigger(
            SESSION_EXPIRES_SOON({
              remainingSeconds: SESSION_WARN_TIMEOUT_SECONDS,
            }),
          );
        }),
      ),
      after(SESSION_WARN_TIMEOUT_SECONDS * 1000, () => {
        eventBus.trigger(SESSION_EXPIRED());
      }),
    );
  },
);

if (ENABLE_LWR) {
  /**
   * Satisfy requests to load Huginn.js in a singleton style.
   * This listener returns an Observable that doesnt complete, and listens in
   * blocking mode, so will not be begun a second time. Makes us over-rendering proof.
   */
  const envVars = env();
  const regexForFF = /(\w?)*_?ENABLE_/;
  trace(
    'FF envs',
    Object.keys(envVars)
      .filter((item) => regexForFF.test(item))
      .map((item) => ({ name: item, value: envVars[item] })),
  );

  eventBus.listenBlocking(HUGINN_LOAD.request.match, huginnLoader);

  // Once loaded, fire off an auth request, blocking others as long as Observable is not #complete
  eventBus.listenBlocking(
    HUGINN_LOAD.complete.match,
    () =>
      new Observable((notify) => {
        eventBus.trigger(LWR_AUTH.request({ path: window.location.href }));
      }),
  );

  // When asked, kick off the process
  eventBus.listenBlocking(LWR_AUTH.request.match, ({ payload: { path } }) => {
    return new Observable(() => {
      provisionSession(path);
    });
  });
}
